import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { 
  GET_CUSTOM_FLAGS_FOR_JOB, 
  ADD_CUSTOM_JOB_FLAG,
  REMOVE_CUSTOM_JOB_FLAG,
  BULK_UPDATE_CUSTOM_JOB_FLAGS
} from '../../graphql/queries/custom-flags';
import { GET_ACTIVE_FLAG_TEMPLATES } from '../../graphql/flagTemplates';
import { 
  validateCustomFlag, 
  sortCustomFlags 
} from '../../utils/custom-flag-utils';
import { CustomFlagIndicator } from '../job-indicators';
import { FlagIcon } from '../../utils/flag-icons';
import { calculateTextColor } from '../../utils/custom-flag-utils';

const CustomFlagManager = ({ job, onFlagChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingFlag, setIsAddingFlag] = useState(false);
  const [newFlag, setNewFlag] = useState({
    flagType: 'category',
    flagValue: '',
    flagColor: '#3b82f6'
  });
  const [editingFlag, setEditingFlag] = useState(null);
  const [selectedFlags, setSelectedFlags] = useState(new Set());

  // GraphQL queries
  const { data: flagsData, loading: flagsLoading, refetch: refetchFlags } = useQuery(GET_CUSTOM_FLAGS_FOR_JOB, {
    variables: { jobId: job.id },
    skip: !job?.id
  });

  const { data: templatesData, loading: templatesLoading } = useQuery(GET_ACTIVE_FLAG_TEMPLATES, {
    skip: !job?.id
  });

  // Flag mutations refresh their own data. The parent modal refreshes job
  // lists separately without resetting the in-progress edit form.
  const [addCustomFlag] = useMutation(ADD_CUSTOM_JOB_FLAG, {
    refetchQueries: [GET_CUSTOM_FLAGS_FOR_JOB],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      // Immediately update the cache with the new flag including template relation
      if (data?.addCustomJobFlag) {
        const newFlag = data.addCustomJobFlag;
        // Update the GET_CUSTOM_FLAGS_FOR_JOB query cache
        try {
          const existingData = cache.readQuery({
            query: GET_CUSTOM_FLAGS_FOR_JOB,
            variables: { jobId: job.id }
          });
          
          if (existingData) {
            cache.writeQuery({
              query: GET_CUSTOM_FLAGS_FOR_JOB,
              variables: { jobId: job.id },
              data: {
                getCustomFlagsForJob: [
                  ...(existingData.getCustomFlagsForJob || []),
                  newFlag
                ]
              }
            });
          }
        } catch (error) {
          // Cache update failed, refetch will handle it
        }
      }
    }
  });
  const [removeCustomFlag] = useMutation(REMOVE_CUSTOM_JOB_FLAG, {
    refetchQueries: [GET_CUSTOM_FLAGS_FOR_JOB],
    awaitRefetchQueries: true
  });
  const [bulkUpdateFlags] = useMutation(BULK_UPDATE_CUSTOM_JOB_FLAGS, {
    refetchQueries: [GET_CUSTOM_FLAGS_FOR_JOB],
    awaitRefetchQueries: true
  });

  const customFlags = sortCustomFlags(flagsData?.getCustomFlagsForJob || []);
  const flagTemplates = templatesData?.getActiveFlagTemplates || [];
  
  // Enhance customFlags with template data if template relation isn't loaded
  const enhancedCustomFlags = customFlags.map(flag => {
    // If template relation isn't loaded but we have templateId, look it up from flagTemplates
    if (!flag.template && flag.templateId && flagTemplates.length > 0) {
      const template = flagTemplates.find(t => t.id === flag.templateId);
      if (template) {
        return { 
          ...flag, 
          template,
          iconName: template.iconName // Ensure iconName is available for CustomFlagIndicator
        };
      }
    }
    // Ensure iconName is available even if template relation is loaded
    if (flag.template && !flag.iconName) {
      return {
        ...flag,
        iconName: flag.template.iconName
      };
    }
    return flag;
  });

  // Handle adding a new flag
  const handleAddFlag = async () => {
    if (!newFlag.flagValue.trim()) return;

    // Validate the flag before adding
    const validation = validateCustomFlag(newFlag, job);
    if (!validation.isValid) {
      alert(`Cannot add flag: ${validation.errors.join(', ')}`);
      return;
    }

    // Show warnings if any
    if (validation.warnings.length > 0) {
      const proceed = window.confirm(
        `Warning: ${validation.warnings.join(', ')}\n\nDo you want to proceed anyway?`
      );
      if (!proceed) return;
    }

    try {
      await addCustomFlag({
        variables: {
          input: {
            jobId: job.id,
            flagType: newFlag.flagType,
            flagValue: newFlag.flagValue.trim(),
            flagColor: newFlag.flagColor,
            sortOrder: customFlags.length
          }
        }
      });
      
      setNewFlag({ flagType: 'category', flagValue: '', flagColor: '#3b82f6' });
      setIsAddingFlag(false);
      refetchFlags();
      onFlagChange?.();
    } catch (error) {
      console.error('Error adding custom flag:', error);
      alert('Error adding flag. Please try again.');
    }
  };

  // Handle removing a flag
  const handleRemoveFlag = async (flagId) => {
    if (!window.confirm('Are you sure you want to remove this flag?')) return;

    try {
      await removeCustomFlag({
        variables: {
          input: { id: flagId }
        }
      });
      
      refetchFlags();
      onFlagChange?.();
    } catch (error) {
      console.error('Error removing custom flag:', error);
      alert('Error removing flag. Please try again.');
    }
  };

  // Handle editing a flag
  const handleEditFlag = async (flagId, updates) => {
    try {
      // For now, we'll remove and re-add the flag since we don't have an update mutation yet
      // This is a temporary solution - in production, you'd want a proper update mutation
      const flag = customFlags.find(f => f.id === flagId);
      if (!flag) return;

      await removeCustomFlag({
        variables: { input: { id: flagId } }
      });

      await addCustomFlag({
        variables: {
          input: {
            jobId: job.id,
            flagType: updates.flagType || flag.flagType,
            flagValue: updates.flagValue || flag.flagValue,
            flagColor: updates.flagColor || flag.flagColor,
            sortOrder: flag.sortOrder
          }
        }
      });

      setEditingFlag(null);
      refetchFlags();
      onFlagChange?.();
    } catch (error) {
      console.error('Error updating custom flag:', error);
      alert('Error updating flag. Please try again.');
    }
  };

  // Handle flag selection for display
  const handleFlagSelection = (flagId) => {
    setSelectedFlags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(flagId)) {
        newSet.delete(flagId);
      } else {
        newSet.add(flagId);
      }
      return newSet;
    });
  };

  // Get all available flag options (existing + templates, no duplicates)
  const getAllAvailableFlags = () => {
    const existingFlags = enhancedCustomFlags.map(flag => ({
      id: flag.id,
      templateId: flag.templateId,
      template: flag.template,
      flagType: flag.flagType,
      flagValue: flag.flagValue,
      flagColor: flag.flagColor,
      iconName: flag.template?.iconName,
      isExisting: true
    }));

    const templateFlags = flagTemplates
      .filter(template => {
        // Only add template if it doesn't already exist as an active flag
        return !enhancedCustomFlags.some(existingFlag => 
          existingFlag.templateId === template.id || 
          (existingFlag.flagValue === template.flagValue && existingFlag.flagType === 'custom')
        );
      })
      .map(template => ({
        id: template.id,
        templateId: template.id,
        template: template,
        flagType: 'custom',
        flagValue: template.flagValue,
        flagColor: template.backgroundColor,
        iconName: template.iconName,
        isExisting: false
      }));

    return [...existingFlags, ...templateFlags];
  };

  const availableFlags = getAllAvailableFlags();

  // Handle adding flag from template
  const handleAddFlagFromTemplate = async (templateFlag) => {
    try {
      await addCustomFlag({
        variables: {
          input: {
            jobId: job.id,
            templateId: templateFlag.templateId || null,
            flagType: templateFlag.flagType || 'custom',
            flagValue: templateFlag.flagValue,
            flagColor: templateFlag.flagColor,
            sortOrder: customFlags.length
          }
        }
      });
      
      refetchFlags();
      onFlagChange?.();
    } catch (error) {
      console.error('Error adding flag from template:', error);
      alert('Error adding flag. Please try again.');
    }
  };

  // Handle bulk adding selected flags
  const handleBulkAddFlags = async () => {
    const selectedFlagObjects = availableFlags.filter(flag => selectedFlags.has(flag.id));
    
    try {
      for (const flag of selectedFlagObjects) {
        if (!flag.isExisting) {
          await addCustomFlag({
            variables: {
              input: {
                jobId: job.id,
                flagType: flag.flagType,
                flagValue: flag.flagValue,
                flagColor: flag.flagColor,
                sortOrder: enhancedCustomFlags.length
              }
            }
          });
        }
      }
      
      setSelectedFlags(new Set());
      refetchFlags();
      onFlagChange?.();
    } catch (error) {
      console.error('Error adding selected flags:', error);
      alert('Error adding flags. Please try again.');
    }
  };

  return (
    <div className="mt-4 border border-gray-200 rounded-lg">
      {/* Accordion Header */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
        className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg border-b border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg 
              className={`w-5 h-5 mr-2 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium text-gray-900">Custom Flags</span>
            {enhancedCustomFlags.length > 0 && (
              <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                {enhancedCustomFlags.length}
              </span>
            )}
          </div>
          <span className="text-sm text-gray-500">
            {isExpanded ? 'Hide' : 'Show'} flags
          </span>
        </div>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="p-4 bg-white">
          {flagsLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading flags...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Manage Job Flags</h4>
                  <p className="text-xs text-gray-500 mt-1">Click an available flag to add it to this job immediately</p>
                </div>
                <div className="text-xs text-gray-500">
                  {selectedFlags.size} selected
                </div>
              </div>

              {/* Current Active Flags */}
              {enhancedCustomFlags.length > 0 && (
                <div>
                  <h5 className="text-xs font-medium text-gray-600 mb-2">Currently Active:</h5>
                  <div className="flex flex-wrap gap-2">
                    {enhancedCustomFlags.map((flag) => (
                      <div key={flag.id} className="inline-flex items-center">
                        <CustomFlagIndicator flag={flag} />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveFlag(flag.id);
                          }}
                          className="ml-2 text-red-500 hover:text-red-700 focus:outline-none"
                          title="Remove flag"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Flags Grid */}
              <div>
                <h5 className="text-xs font-medium text-gray-600 mb-3">Available Flags:</h5>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                  {availableFlags.map((flag) => {
                    const isSelected = selectedFlags.has(flag.id);
                    const isActive = customFlags.some(f => f.id === flag.id);
                    
                    return (
                      <button
                        key={flag.id}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!flag.isExisting) {
                            // For template flags, add them as new flags
                            handleAddFlagFromTemplate(flag);
                          } else {
                            // For existing flags, toggle selection
                            handleFlagSelection(flag.id);
                          }
                        }}
                        disabled={isActive}
                        className={`relative p-3 rounded-lg border-2 text-left transition-all duration-200 ${
                          isActive 
                            ? 'border-green-300 bg-green-50 opacity-75 cursor-not-allowed' 
                            : isSelected 
                              ? 'border-blue-500 bg-blue-50 shadow-md' 
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                        title={isActive ? 'Already active' : 'Add flag'}
                      >
                        {/* Flag Preview */}
                        <div className="w-full h-8 rounded mb-2 flex items-center justify-center">
                          {/* Use CustomFlagIndicator for both existing and template flags to ensure consistent styling */}
                          <CustomFlagIndicator flag={flag} />
                        </div>
                        
                        {/* Flag Info */}
                        <div className="text-xs text-gray-600">
                          <div className="font-medium capitalize">{flag.flagType}</div>
                          <div className="truncate">{flag.flagValue}</div>
                        </div>

                        {/* Selection Indicator */}
                        {isSelected && !isActive && (
                          <div className="absolute top-1 right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}

                        {/* Active Indicator */}
                        {isActive && (
                          <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedFlags.size > 0 && (
                <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedFlags(new Set());
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 focus:outline-none"
                  >
                    Clear Selection
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBulkAddFlags();
                    }}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Add Selected ({selectedFlags.size})
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomFlagManager;
